import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';
import PageTransition from '../components/PageTransition';

// ── OptiKa palette ────────────────────────────────────────────────────────────

const C = {
  ink:     '#0B0E17',
  surface: '#1E2235',
  border:  '#2A2E45',
  muted:   '#8892A4',
  gold:    '#F0B429',
  teal:    '#00C9B1',
  coral:   '#FF6B6B',
  lavender:'#A78BFA',
  sky:     '#38BDF8',
  text:    '#E8EDF5',
  sub:     '#C5CDD8',
};

// ── CSS ───────────────────────────────────────────────────────────────────────

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Instrument+Serif:ital@1&display=swap');

  .rp-page * { box-sizing: border-box; }

  .rp-page {
    font-family: 'Cabinet Grotesk', 'Inter', system-ui, sans-serif;
    background: ${C.ink};
    min-height: 100vh;
    color: ${C.text};
  }

  .rp-mono { font-family: 'JetBrains Mono', monospace; }
  .rp-serif { font-family: 'Instrument Serif', serif; font-style: italic; }

  .rp-card {
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 14px;
    overflow: hidden;
  }

  .rp-card-header {
    padding: 16px 20px;
    border-bottom: 1px solid ${C.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .rp-card-body { padding: 20px; }

  .rp-tab-btn {
    padding: 12px 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
    transition: all 0.2s;
    outline: none;
    white-space: nowrap;
    color: ${C.muted};
    font-family: 'Cabinet Grotesk', sans-serif;
  }

  .rp-tab-btn.active {
    color: ${C.gold};
    border-bottom-color: ${C.gold};
    background: rgba(240,180,41,0.06);
    border-radius: 10px 10px 0 0;
  }

  .rp-tab-btn:hover:not(.active) { color: ${C.text}; }

  .rp-report-card {
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 14px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: all 0.25s ease;
    cursor: default;
  }

  .rp-report-card:hover {
    border-color: ${C.gold};
    box-shadow: 0 8px 32px rgba(240,180,41,0.10);
    transform: translateY(-2px);
  }

  .rp-icon-box {
    width: 48px; height: 48px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; flex-shrink: 0;
  }

  .rp-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 100px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.03em;
  }

  .rp-dl-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 14px; border-radius: 8px; border: none;
    font-size: 11px; font-weight: 700; cursor: pointer;
    transition: all 0.18s; white-space: nowrap;
    font-family: 'Cabinet Grotesk', sans-serif;
    background: ${C.gold}; color: #000;
  }

  .rp-dl-btn:disabled { background: #3A3F55; color: ${C.muted}; cursor: not-allowed; }
  .rp-dl-btn:not(:disabled):hover { background: #E0A520; }

  .rp-dl-btn.secondary {
    background: transparent;
    border: 1px solid ${C.border};
    color: ${C.text};
  }
  .rp-dl-btn.secondary:not(:disabled):hover {
    border-color: ${C.gold};
    color: ${C.gold};
    background: rgba(240,180,41,0.06);
  }

  .rp-select {
    padding: 7px 11px; border-radius: 8px;
    border: 1px solid ${C.border};
    font-size: 12px; font-weight: 600;
    background: ${C.surface}; color: ${C.text};
    outline: none; cursor: pointer;
    font-family: 'Cabinet Grotesk', sans-serif;
    transition: border-color 0.15s;
  }
  .rp-select:focus { border-color: ${C.gold}; }

  .rp-input {
    width: 100%; padding: 9px 13px; border-radius: 9px;
    border: 1px solid ${C.border}; font-size: 13px;
    color: ${C.text}; background: rgba(255,255,255,0.04);
    outline: none; box-sizing: border-box;
    font-family: 'Cabinet Grotesk', sans-serif;
    transition: border-color 0.15s;
  }
  .rp-input:focus { border-color: ${C.gold}; }

  .rp-fmt-btn {
    padding: 4px 9px; border-radius: 6px; font-size: 10px; font-weight: 700;
    cursor: pointer; transition: all 0.15s;
    font-family: 'Cabinet Grotesk', sans-serif;
  }

  .rp-stat-tile {
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 14px;
    padding: 18px;
    min-height: 108px;
    display: flex; flex-direction: column;
    justify-content: space-between; gap: 8px;
    transition: border-color 0.2s;
  }
  .rp-stat-tile:hover { border-color: rgba(240,180,41,0.3); }

  .rp-bar-track {
    height: 7px; border-radius: 999px;
    background: rgba(255,255,255,0.07);
    overflow: hidden; margin-top: 6px;
  }

  .rp-bar-fill {
    height: 100%; border-radius: 999px;
    transition: width 0.6s ease;
  }

  .rp-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .rp-table th {
    padding: 11px 16px; text-align: left; font-weight: 600;
    font-size: 11px; white-space: nowrap;
    background: rgba(240,180,41,0.08);
    color: ${C.gold}; border-bottom: 1px solid ${C.border};
    letter-spacing: 0.04em;
  }
  .rp-table td {
    padding: 11px 16px; border-bottom: 1px solid rgba(42,46,69,0.5);
    color: ${C.sub};
  }
  .rp-table tr:hover td { background: rgba(255,255,255,0.02); }

  .rp-checklist-item {
    border-radius: 10px; padding: 10px 14px;
    display: flex; align-items: flex-start; gap: 12px;
  }

  .rp-icon-circle {
    width: 26px; height: 26px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; flex-shrink: 0; margin-top: 1px;
    background: rgba(255,255,255,0.12);
  }

  .rp-hero-box {
    background: linear-gradient(135deg, rgba(0,201,177,0.12) 0%, rgba(56,189,248,0.08) 100%);
    border: 1px solid rgba(0,201,177,0.2);
    border-radius: 12px; padding: 16px 20px; margin-bottom: 18px;
  }

  .rp-preview-panel {
    background: rgba(255,255,255,0.02);
    border: 2px dashed ${C.border};
    border-radius: 14px; padding: 24px;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    min-height: 320px;
  }

  .rp-preview-panel.has-result {
    align-items: flex-start;
    justify-content: flex-start;
  }

  .rp-quick-pill {
    background: rgba(255,255,255,0.05);
    border: 1px solid ${C.border};
    border-radius: 999px;
    padding: 8px 13px;
    display: flex; gap: 8px; align-items: center;
  }

  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes rp-fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  .rp-toast {
    position: fixed; bottom: 28px; right: 28px; z-index: 9999;
    border-radius: 10px; padding: 10px 18px;
    font-size: 13px; font-weight: 600;
    box-shadow: 0 4px 24px rgba(0,0,0,0.4);
    animation: rp-fadeUp 0.25s ease;
    font-family: 'Cabinet Grotesk', sans-serif;
  }

  .rp-pl-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 9px 0;
  }
  .rp-pl-row + .rp-pl-row { border-top: 1px solid ${C.border}; }

  .rp-section-label {
    font-size: 11px; font-weight: 700; color: ${C.gold};
    letter-spacing: 0.08em; text-transform: uppercase;
    margin-bottom: 14px;
  }

  .rp-empty {
    text-align: center; padding: 60px 24px;
  }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildApiUrl(path) {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  return `${base}${path}`;
}

async function downloadBlob(url, filename, method = 'GET', body = null) {
  const opts = {
    method,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  if (!res.ok) {
    const text = await res.text();
    let safeMessage = 'Failed to download report. Please try again.';
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed.error === 'string' && parsed.error) safeMessage = parsed.error;
    } catch {
      // response body wasn't JSON (e.g. an HTML error page) — keep the generic message
    }
    console.error('[Reports] download failed:', res.status, text);
    throw new Error(safeMessage);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      month: d.getMonth() + 1,
      year:  d.getFullYear(),
    });
  }
  return opts;
}

function quarterOptions() {
  const opts = [];
  const now  = new Date();
  let q = Math.ceil((now.getMonth() + 1) / 3);
  let y = now.getFullYear();
  for (let i = 0; i < 8; i++) {
    opts.push({ value: `${y}-Q${q}`, label: `Q${q} ${y}`, quarter: q, year: y });
    q--;
    if (q === 0) { q = 4; y--; }
  }
  return opts;
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtCurrency(n) {
  const num = Number(n || 0);
  const abs = Math.abs(num);
  const str = `GHS ${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return num < 0 ? `-${str}` : str;
}
function fmtSize(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// ── Shared primitives ─────────────────────────────────────────────────────────

const BADGE_META = {
  green:  { bg: 'rgba(0,201,177,0.15)',  color: '#00C9B1' },
  blue:   { bg: 'rgba(56,189,248,0.15)', color: '#38BDF8' },
  gold:   { bg: 'rgba(240,180,41,0.15)', color: '#F0B429' },
  red:    { bg: 'rgba(255,107,107,0.15)',color: '#FF6B6B' },
  purple: { bg: 'rgba(167,139,250,0.15)',color: '#A78BFA' },
  navy:   { bg: 'rgba(56,189,248,0.12)', color: '#38BDF8' },
  gray:   { bg: 'rgba(255,255,255,0.08)',color: '#8892A4' },
};

function Badge({ variant = 'gray', children, style }) {
  const meta = BADGE_META[variant] || BADGE_META.gray;
  return (
    <span className="rp-badge" style={{ background: meta.bg, color: meta.color, ...style }}>
      {children}
    </span>
  );
}

function SpinIcon({ color = C.gold, size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

function DownloadBtn({ loading, onClick, children = '⬇ Download', secondary, style }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`rp-dl-btn${secondary ? ' secondary' : ''}`}
      style={style}
    >
      {loading ? <SpinIcon size={12} color={secondary ? C.muted : '#000'} /> : null}
      {children}
    </button>
  );
}

function SmallSelect({ value, onChange, options, style }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="rp-select" style={style}>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function SectionCard({ title, action, children, style }) {
  return (
    <div className="rp-card" style={style}>
      <div className="rp-card-header">
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{title}</div>
        {action}
      </div>
      <div className="rp-card-body">{children}</div>
    </div>
  );
}

function StatTile({ label, value, sub, color = C.teal }) {
  return (
    <div className="rp-stat-tile">
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div className="rp-mono" style={{ fontSize: 19, fontWeight: 700, color }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: C.muted }}>{sub}</div> : <div />}
    </div>
  );
}

function BarList({ rows, color = C.teal, empty = 'No data yet', valueLabel }) {
  if (!rows?.length) {
    return <div style={{ fontSize: 12, color: C.muted }}>{empty}</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {rows.map((row) => (
        <div key={row.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{row.label}</div>
            <div className="rp-mono" style={{ fontSize: 12, fontWeight: 700, color }}>{valueLabel(row)}</div>
          </div>
          <div className="rp-bar-track">
            <div className="rp-bar-fill" style={{
              width: `${Math.max(Math.min(row.pct || 0, 100), (row.amount || row.revenue || 0) > 0 ? 5 : 0)}%`,
              background: color,
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function useToast() {
  const [toast, setToast] = useState(null);
  function show(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }
  return { toast, show };
}

function Toast({ toast }) {
  if (!toast) return null;
  const isError = toast.type === 'error';
  return (
    <div className="rp-toast" style={{
      background: isError ? 'rgba(255,107,107,0.15)' : 'rgba(0,201,177,0.15)',
      color: isError ? C.coral : C.teal,
      border: `1px solid ${isError ? 'rgba(255,107,107,0.3)' : 'rgba(0,201,177,0.3)'}`,
    }}>
      {toast.msg}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — REPORT CENTER
// ═══════════════════════════════════════════════════════════════════════════════

function ReportCard({ iconBg, icon, name, badges, desc, footer }) {
  return (
    <div className="rp-report-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="rp-icon-box" style={{ background: iconBg }}>{icon}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 5 }}>{name}</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{badges}</div>
        </div>
      </div>
      <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, margin: 0,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {desc}
      </p>
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 'auto' }}>
        {footer}
      </div>
    </div>
  );
}

function ReportCenterTab({ onToast }) {
  const MONTHS = monthOptions();
  const QUARTERS = quarterOptions();

  const [plPeriod,  setPlPeriod]  = useState(MONTHS[0].value);
  const [salPeriod, setSalPeriod] = useState(MONTHS[0].value);
  const [expPeriod, setExpPeriod] = useState(MONTHS[0].value);
  const [vatPeriod, setVatPeriod] = useState(QUARTERS[0].value);

  const [invFmt,  setInvFmt]  = useState('pdf');
  const [salFmt,  setSalFmt]  = useState('pdf');
  const [custFmt, setCustFmt] = useState('pdf');
  const [expFmt,  setExpFmt]  = useState('pdf');

  const [loading, setLoading] = useState({});
  function setLoad(key, val) { setLoading(l => ({ ...l, [key]: val })); }

  function parsePeriod(val) {
    const [y, m] = val.split('-');
    return { year: parseInt(y), month: parseInt(m) };
  }
  function parseQuarter(val) {
    const [y, qStr] = val.split('-Q');
    return { year: parseInt(y), quarter: parseInt(qStr) };
  }

  async function dlPL() {
    setLoad('pl', true);
    try {
      const { month, year } = parsePeriod(plPeriod);
      await downloadBlob(buildApiUrl(`/reports/pl/pdf?month=${month}&year=${year}`), `pl-${year}-${String(month).padStart(2,'0')}.pdf`);
      onToast('P&L Statement downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLoad('pl', false); }
  }

  async function dlInventory() {
    setLoad('inv', true);
    try {
      const ext = invFmt === 'excel' ? 'xlsx' : 'pdf';
      await downloadBlob(buildApiUrl(`/reports/inventory/${invFmt}`), `inventory-report.${ext}`);
      onToast('Inventory Report downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLoad('inv', false); }
  }

  async function dlSales() {
    setLoad('sal', true);
    try {
      const { month, year } = parsePeriod(salPeriod);
      const start = new Date(year, month - 1, 1).toISOString().slice(0, 10);
      const end   = new Date(year, month,     0).toISOString().slice(0, 10);
      const ext = salFmt === 'excel' ? 'xlsx' : 'pdf';
      await downloadBlob(buildApiUrl(`/reports/sales/${salFmt}?startDate=${start}&endDate=${end}`), `sales-report.${ext}`);
      onToast('Sales Report downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLoad('sal', false); }
  }

  async function dlCustomers() {
    setLoad('cust', true);
    try {
      const ext = custFmt === 'excel' ? 'xlsx' : 'pdf';
      await downloadBlob(buildApiUrl(`/reports/customers/${custFmt}`), `customer-report.${ext}`);
      onToast('Customer Report downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLoad('cust', false); }
  }

  async function dlExpenses() {
    setLoad('exp', true);
    try {
      const { month, year } = parsePeriod(expPeriod);
      const start = new Date(year, month - 1, 1).toISOString().slice(0, 10);
      const end   = new Date(year, month,     0).toISOString().slice(0, 10);
      const ext = expFmt === 'excel' ? 'xlsx' : 'pdf';
      await downloadBlob(buildApiUrl(`/reports/expenses/${expFmt}?startDate=${start}&endDate=${end}`), `expense-report.${ext}`);
      onToast('Expense Report downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLoad('exp', false); }
  }

  async function dlVAT() {
    setLoad('vat', true);
    try {
      const { quarter, year } = parseQuarter(vatPeriod);
      await downloadBlob(buildApiUrl(`/reports/vat/pdf?quarter=${quarter}&year=${year}`), `vat-Q${quarter}-${year}.pdf`);
      onToast('VAT Report downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLoad('vat', false); }
  }

  function FmtToggle({ fmt, setFmt, activeColor }) {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        {['pdf', 'excel'].map(f => (
          <button key={f} onClick={() => setFmt(f)} className="rp-fmt-btn" style={{
            border: `1px solid ${fmt === f ? activeColor : C.border}`,
            background: fmt === f ? `${activeColor}22` : 'transparent',
            color: fmt === f ? activeColor : C.muted,
          }}>
            {f.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="rp-section-label">Quick Reports</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>

        <ReportCard
          iconBg="rgba(0,201,177,0.12)" icon="📊"
          name="P&L Statement"
          badges={<Badge variant="green">PDF</Badge>}
          desc="Full income statement with revenue, COGS, gross profit, expenses and net profit."
          footer={<>
            <SmallSelect value={plPeriod} onChange={setPlPeriod} options={MONTHS} />
            <DownloadBtn loading={loading.pl} onClick={dlPL} />
          </>}
        />

        <ReportCard
          iconBg="rgba(56,189,248,0.12)" icon="📦"
          name="Inventory Report"
          badges={<><Badge variant="blue">PDF</Badge><Badge variant="blue" style={{ marginLeft: 4 }}>Excel</Badge></>}
          desc="Complete stock list with quantities, costs, margins and low stock alerts."
          footer={<>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Badge variant="navy">Current stock</Badge>
              <FmtToggle fmt={invFmt} setFmt={setInvFmt} activeColor={C.sky} />
            </div>
            <DownloadBtn loading={loading.inv} onClick={dlInventory} />
          </>}
        />

        <ReportCard
          iconBg="rgba(240,180,41,0.12)" icon="💰"
          name="Sales Report"
          badges={<><Badge variant="gold">PDF</Badge><Badge variant="gold" style={{ marginLeft: 4 }}>Excel</Badge></>}
          desc="All transactions with customer names, items, totals and payment methods."
          footer={<>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <SmallSelect value={salPeriod} onChange={setSalPeriod} options={MONTHS} />
              <FmtToggle fmt={salFmt} setFmt={setSalFmt} activeColor={C.gold} />
            </div>
            <DownloadBtn loading={loading.sal} onClick={dlSales} />
          </>}
        />

        <ReportCard
          iconBg="rgba(167,139,250,0.12)" icon="👥"
          name="Customer Report"
          badges={<><Badge variant="purple">PDF</Badge><Badge variant="purple" style={{ marginLeft: 4 }}>Excel</Badge></>}
          desc="Customer list with spend, loyalty points, segments and outstanding debts."
          footer={<>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Badge variant="navy">All customers</Badge>
              <FmtToggle fmt={custFmt} setFmt={setCustFmt} activeColor={C.lavender} />
            </div>
            <DownloadBtn loading={loading.cust} onClick={dlCustomers} />
          </>}
        />

        <ReportCard
          iconBg="rgba(255,107,107,0.12)" icon="💸"
          name="Expense Report"
          badges={<><Badge variant="red">PDF</Badge><Badge variant="red" style={{ marginLeft: 4 }}>Excel</Badge></>}
          desc="Operating expenses by category with totals and budget comparison."
          footer={<>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <SmallSelect value={expPeriod} onChange={setExpPeriod} options={MONTHS} />
              <FmtToggle fmt={expFmt} setFmt={setExpFmt} activeColor={C.coral} />
            </div>
            <DownloadBtn loading={loading.exp} onClick={dlExpenses} />
          </>}
        />

        <ReportCard
          iconBg="rgba(0,201,177,0.12)" icon="🧾"
          name="VAT Report"
          badges={<Badge variant="green">PDF</Badge>}
          desc="VAT collected, input VAT on purchases, and net VAT payable to GRA."
          footer={<>
            <SmallSelect value={vatPeriod} onChange={setVatPeriod} options={QUARTERS} />
            <DownloadBtn loading={loading.vat} onClick={dlVAT} />
          </>}
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — TAX SUMMARY
// ═══════════════════════════════════════════════════════════════════════════════

function AnimatedCurrency({ value }) {
  const [displayed, setDisplayed] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const target = Number(value || 0);
    const duration = 900;
    const start = performance.now();
    function step(now) {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplayed(target * ease);
      if (t < 1) ref.current = requestAnimationFrame(step);
    }
    ref.current = requestAnimationFrame(step);
    return () => { if (ref.current) cancelAnimationFrame(ref.current); };
  }, [value]);
  return (
    <span>
      {`GHS ${displayed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
    </span>
  );
}

function TaxSummaryTab({ onToast }) {
  const QUARTERS = quarterOptions();
  const [period, setPeriod]     = useState(QUARTERS[0].value);
  const [data, setData]         = useState(null);
  const [vatSettings, setVat]   = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [dlLoading, setDlLoading]    = useState(false);

  function parseQ(val) {
    const [y, qStr] = val.split('-Q');
    return { year: parseInt(y), quarter: parseInt(qStr) };
  }

  useEffect(() => {
    async function fetchData() {
      setLoadingData(true);
      try {
        const { quarter, year } = parseQ(period);
        const startMonth = (quarter - 1) * 3 + 1;
        const months = [startMonth, startMonth + 1, startMonth + 2];
        const [vatRes, ...plResults] = await Promise.all([
          api.get('/financials/vat/settings').catch(() => ({ data: {} })),
          ...months.map(m => api.get(`/financials/pl?month=${m}&year=${year}`).catch(() => ({ data: {} }))),
        ]);
        setVat(vatRes.data);
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        setData({
          quarter, year,
          months: months.map((m, i) => ({
            name: monthNames[m - 1],
            revenue: plResults[i].data?.revenue || 0,
            cogs: plResults[i].data?.cogs || 0,
            expenses: plResults[i].data?.totalExpenses || 0,
            netProfit: plResults[i].data?.netProfit || 0,
          })),
        });
      } catch (_) {}
      finally { setLoadingData(false); }
    }
    fetchData();
  }, [period]);

  const totalRevenue  = data?.months.reduce((s, m) => s + m.revenue, 0) || 0;
  const totalCogs     = data?.months.reduce((s, m) => s + m.cogs, 0) || 0;
  const totalExpenses = data?.months.reduce((s, m) => s + m.expenses, 0) || 0;
  const netProfit     = totalRevenue - totalCogs - totalExpenses;

  async function dlTax() {
    setDlLoading(true);
    try {
      const { year } = parseQ(period);
      await downloadBlob(buildApiUrl(`/reports/tax/pdf?year=${year}`), `tax-summary-${year}.pdf`);
      onToast('Tax Summary PDF downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setDlLoading(false); }
  }

  const qLabel = period.replace('-', ' ').replace('Q', 'Q');
  const vatEnabled = vatSettings?.vatEnabled;

  const checklist = [
    { icon: '✓', text: 'Revenue recorded', sub: 'All sales captured in the system', bg: 'rgba(0,201,177,0.10)', iconColor: C.teal },
    { icon: '✓', text: 'Expenses documented', sub: 'All costs recorded', bg: 'rgba(0,201,177,0.10)', iconColor: C.teal },
    vatEnabled
      ? { icon: '✓', text: 'VAT registration active', sub: `Rate: ${vatSettings.vatRate}%${vatSettings.vatNumber ? ` | TIN: ${vatSettings.vatNumber}` : ''}`, bg: 'rgba(0,201,177,0.10)', iconColor: C.teal }
      : { icon: '⚠', text: 'VAT registration pending', sub: 'Enable VAT in Financial settings', bg: 'rgba(240,180,41,0.10)', iconColor: C.gold },
    { icon: '⚠', text: 'Income tax return', sub: `Due: 31 March ${(data?.year || new Date().getFullYear()) + 1}`, bg: 'rgba(240,180,41,0.10)', iconColor: C.gold },
    { icon: 'ℹ', text: 'SSNIT contributions', sub: 'Ensure staff is enrolled and covered', bg: 'rgba(56,189,248,0.10)', iconColor: C.sky },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: C.muted }}>Period:</span>
        <SmallSelect value={period} onChange={setPeriod} options={QUARTERS} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* LEFT — Tax Summary */}
        <div className="rp-card" style={{ padding: 22 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>
            Tax Summary — {qLabel}
          </div>

          <div className="rp-hero-box">
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.07em', marginBottom: 6, textTransform: 'uppercase' }}>
              TOTAL REVENUE ({qLabel})
            </div>
            <div className="rp-mono" style={{ fontSize: 26, fontWeight: 700, color: C.teal, lineHeight: 1 }}>
              {loadingData ? '—' : <AnimatedCurrency value={totalRevenue} />}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Across 3 months</div>
          </div>

          {loadingData ? (
            <div style={{ textAlign: 'center', padding: 20, color: C.muted, fontSize: 13 }}>Loading…</div>
          ) : (
            <>
              {data?.months.map(m => (
                <div key={m.name} className="rp-pl-row">
                  <span style={{ fontSize: 13, color: C.muted }}>{m.name} Revenue</span>
                  <span className="rp-mono" style={{ fontSize: 13, fontWeight: 600, color: C.sub }}>{fmtCurrency(m.revenue)}</span>
                </div>
              ))}
              <div className="rp-pl-row">
                <span style={{ fontSize: 13, color: C.muted }}>Total COGS</span>
                <span className="rp-mono" style={{ fontSize: 13, fontWeight: 600, color: C.coral }}>-{fmtCurrency(totalCogs)}</span>
              </div>
              <div className="rp-pl-row">
                <span style={{ fontSize: 13, color: C.muted }}>Total OpEx</span>
                <span className="rp-mono" style={{ fontSize: 13, fontWeight: 600, color: C.coral }}>-{fmtCurrency(totalExpenses)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0', borderTop: `2px solid ${C.border}`, marginTop: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>NET PROFIT ({qLabel})</span>
                <span className="rp-mono" style={{ fontSize: 16, fontWeight: 800, color: netProfit >= 0 ? C.teal : C.coral }}>
                  {fmtCurrency(netProfit)}
                </span>
              </div>
            </>
          )}

          <button
            onClick={dlTax}
            disabled={dlLoading}
            style={{
              width: '100%', marginTop: 14,
              background: dlLoading ? '#3A3F55' : C.gold,
              color: dlLoading ? C.muted : '#000',
              padding: '10px 0', borderRadius: 10,
              fontSize: 13, fontWeight: 700, border: 'none',
              cursor: dlLoading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 0.18s', fontFamily: 'Cabinet Grotesk, sans-serif',
            }}
          >
            {dlLoading ? <SpinIcon size={14} color={C.muted} /> : null}
            {dlLoading ? 'Downloading…' : '⬇ Download Tax Summary PDF'}
          </button>
        </div>

        {/* RIGHT — GRA Checklist */}
        <div className="rp-card" style={{ padding: 22 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>
            GRA Filing Checklist
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {checklist.map((item, i) => (
              <div key={i} className="rp-checklist-item" style={{ background: item.bg }}>
                <div className="rp-icon-circle" style={{ color: item.iconColor }}>{item.icon}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{item.text}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20, padding: '12px 14px', background: 'rgba(255,255,255,0.03)',
            borderRadius: 10, fontSize: 11, color: C.muted, border: `1px solid ${C.border}` }}>
            GRA portal: <strong style={{ color: C.sub }}>ghaportal.com</strong> · Tax helpline: <strong style={{ color: C.sub }}>0800 900 110</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — CUSTOM BUILDER
// ═══════════════════════════════════════════════════════════════════════════════

const CUSTOM_TYPES = [
  { value: 'pl',        label: 'P&L Statement' },
  { value: 'sales',     label: 'Sales Report' },
  { value: 'inventory', label: 'Inventory Report' },
  { value: 'customers', label: 'Customer Report' },
  { value: 'expenses',  label: 'Expense Report' },
  { value: 'vat',       label: 'VAT Report' },
];

const GROUP_OPTIONS = {
  pl:        [{ value: 'month', label: 'Month' }, { value: 'quarter', label: 'Quarter' }, { value: 'year', label: 'Year' }],
  sales:     [{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }, { value: 'category', label: 'Category' }, { value: 'customer', label: 'Customer' }],
  inventory: [{ value: 'category', label: 'Category' }, { value: 'supplier', label: 'Supplier' }],
  customers: [{ value: 'segment', label: 'Segment' }, { value: 'pricingTier', label: 'Pricing Tier' }, { value: 'month', label: 'Join Month' }],
  expenses:  [{ value: 'day', label: 'Day' }, { value: 'month', label: 'Month' }, { value: 'category', label: 'Category' }],
  vat:       [{ value: 'month', label: 'Month' }, { value: 'quarter', label: 'Quarter' }],
};

const FORMATS = [
  { value: 'pdf',   label: '📄 PDF',   ext: 'pdf' },
  { value: 'excel', label: '📊 Excel', ext: 'xlsx' },
];

function today() { return new Date().toISOString().slice(0, 10); }
function monthStart() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function CustomBuilderTab({ onToast }) {
  const [reportType, setReportType] = useState('sales');
  const [startDate, setStartDate]   = useState(monthStart());
  const [endDate, setEndDate]       = useState(today());
  const [groupBy, setGroupBy]       = useState('month');
  const [format, setFormat]         = useState('pdf');
  const [loading, setLoading]       = useState(false);
  const [success, setSuccess]       = useState(false);

  async function generate() {
    setLoading(true);
    setSuccess(false);
    try {
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      await downloadBlob(
        buildApiUrl('/reports/custom'),
        `custom-${reportType}-report.${ext}`,
        'POST',
        { reportType, startDate, endDate, groupBy, format },
      );
      setSuccess(true);
      onToast('Custom report downloaded!');
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLoading(false); }
  }

  const typeLabel  = CUSTOM_TYPES.find(t => t.value === reportType)?.label || '';
  const groupLabel = GROUP_OPTIONS[reportType]?.find(g => g.value === groupBy)?.label || '';

  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: C.muted,
    letterSpacing: '0.07em', marginBottom: 7,
    display: 'block', textTransform: 'uppercase',
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* LEFT — Form */}
      <div className="rp-card" style={{ padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 20 }}>Build Custom Report</div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Report Type</label>
          <select value={reportType}
            onChange={e => { setReportType(e.target.value); setGroupBy('month'); }}
            className="rp-input" style={{ cursor: 'pointer' }}>
            {CUSTOM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <div>
            <label style={labelStyle}>From Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="rp-input" />
          </div>
          <div>
            <label style={labelStyle}>To Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="rp-input" />
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Group By</label>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
            className="rp-input" style={{ cursor: 'pointer' }}>
            {(GROUP_OPTIONS[reportType] || []).map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 22 }}>
          <label style={labelStyle}>Export Format</label>
          <div style={{ display: 'flex', gap: 10 }}>
            {FORMATS.map(f => (
              <button key={f.value} onClick={() => setFormat(f.value)} style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                border: `2px solid ${format === f.value ? C.gold : C.border}`,
                background: format === f.value ? 'rgba(240,180,41,0.12)' : 'transparent',
                color: format === f.value ? C.gold : C.muted,
                fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.18s',
                fontFamily: 'Cabinet Grotesk, sans-serif',
              }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={generate}
          disabled={loading}
          style={{
            width: '100%',
            background: loading ? '#3A3F55' : C.gold,
            color: loading ? C.muted : '#000',
            padding: '11px 0', borderRadius: 10,
            fontSize: 14, fontWeight: 700, border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'background 0.18s', fontFamily: 'Cabinet Grotesk, sans-serif',
          }}
        >
          {loading ? <SpinIcon size={14} color={C.muted} /> : null}
          {loading ? 'Generating…' : 'Generate Report'}
        </button>
      </div>

      {/* RIGHT — Preview */}
      <div className={`rp-preview-panel${success ? ' has-result' : ''}`}>
        {success ? (
          <div style={{ width: '100%' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>Report Preview</div>
            <div className="rp-card" style={{ overflow: 'hidden' }}>
              <div style={{ background: 'rgba(240,180,41,0.12)', borderBottom: `1px solid ${C.border}`,
                color: C.gold, padding: '10px 16px', fontSize: 12, fontWeight: 700 }}>
                {typeLabel}
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Report type', value: typeLabel },
                  { label: 'Period', value: `${startDate} → ${endDate}` },
                  { label: 'Grouped by', value: groupLabel },
                  { label: 'Format', value: format.toUpperCase() },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: C.muted }}>{row.label}</span>
                    <span style={{ fontWeight: 600, color: C.text }}>{row.value}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: 'rgba(0,201,177,0.12)', padding: '10px 16px',
                borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: C.teal, fontSize: 12, fontWeight: 600 }}>✓ Downloaded successfully!</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: C.muted }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Report Preview</div>
            <div style={{ fontSize: 12, color: '#5A6172', maxWidth: 200, lineHeight: 1.5 }}>
              Configure options on the left and click Generate
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 4 — WHOLESALE INSIGHTS
// ═══════════════════════════════════════════════════════════════════════════════

function WholesaleInsightsTab({ onToast }) {
  const MONTHS = monthOptions();
  const [period, setPeriod]   = useState(MONTHS[0].value);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  function parsePeriod(value) {
    const [year, month] = value.split('-');
    return { year: parseInt(year, 10), month: parseInt(month, 10) };
  }

  useEffect(() => {
    const { month, year } = parsePeriod(period);
    setLoading(true);
    api.get(`/reports/wholesale/summary?month=${month}&year=${year}`)
      .then((res) => setSummary(res.data))
      .catch((err) => {
        setSummary(null);
        onToast(err.response?.data?.error || 'Failed to load wholesale report', 'error');
      })
      .finally(() => setLoading(false));
  }, [period]);

  async function downloadWholesale(format) {
    const { month, year } = parsePeriod(period);
    setDownloading((cur) => ({ ...cur, [format]: true }));
    try {
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      await downloadBlob(
        buildApiUrl(`/reports/wholesale/${format}?month=${month}&year=${year}`),
        `wholesale-${year}-${String(month).padStart(2, '0')}.${ext}`,
      );
      onToast(`Wholesale ${format.toUpperCase()} downloaded`);
    } catch (error) { onToast(error.message, 'error'); }
    finally { setDownloading((cur) => ({ ...cur, [format]: false })); }
  }

  const stats = summary ? [
    { label: 'Net Sales',          value: fmtCurrency(summary.overview.netSales),              sub: `${summary.overview.salesCount} sales`,                                              color: C.teal },
    { label: 'Gross Profit',       value: fmtCurrency(summary.overview.grossProfit),           sub: `${Number(summary.overview.grossMargin || 0).toFixed(1)}% margin`,                  color: C.sky },
    { label: 'Checkout Collected', value: fmtCurrency(summary.overview.checkoutCollected),     sub: 'At sale time',                                                                      color: C.lavender },
    { label: 'Collections In',     value: fmtCurrency(summary.overview.collectionsReceived),   sub: 'Debt repayments',                                                                   color: C.teal },
    { label: 'Customer Balance',   value: fmtCurrency(summary.overview.openCustomerBalance),   sub: `${fmtCurrency(summary.overview.overdueCustomerBalance)} overdue`,                   color: C.gold },
    { label: 'Supplier Bills',     value: fmtCurrency(summary.overview.supplierOutstanding),   sub: `${fmtCurrency(summary.overview.supplierSpend)} ordered`,                            color: C.coral },
  ] : [];

  const quickFigures = summary ? [
    { label: 'Units',        value: Number(summary.overview.itemsSold || 0).toLocaleString('en-US') },
    { label: 'Returns',      value: fmtCurrency(summary.overview.returnsValue) },
    { label: 'Credit Issued',value: fmtCurrency(summary.overview.creditIssued) },
    { label: 'Supplier Paid',value: fmtCurrency(summary.overview.supplierPaymentsReceived) },
  ] : [];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <SectionCard
        title="Wholesale Insights"
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <SmallSelect value={period} onChange={setPeriod} options={MONTHS} style={{ minWidth: 180 }} />
            <DownloadBtn loading={downloading.pdf} onClick={() => downloadWholesale('pdf')}>⬇ PDF</DownloadBtn>
            <DownloadBtn loading={downloading.excel} onClick={() => downloadWholesale('excel')}
              style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.text }}>
              ⬇ Excel
            </DownloadBtn>
          </div>
        }
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div className="rp-serif" style={{ fontSize: 22, color: C.gold, marginBottom: 4 }}>
              {summary?.period?.label || 'Wholesale pulse'}
            </div>
            <div style={{ fontSize: 13, color: C.muted }}>
              Sales, collections, supplier exposure, and tier performance in one view.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {quickFigures.map((entry) => (
              <div key={entry.label} className="rp-quick-pill">
                <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{entry.label}</span>
                <span className="rp-mono" style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
          <SpinIcon size={28} color={C.gold} />
        </div>
      ) : !summary ? (
        <SectionCard title="Wholesale Insights">
          <div style={{ fontSize: 13, color: C.muted }}>No report data available for this period.</div>
        </SectionCard>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            {stats.map((entry) => (
              <StatTile key={entry.label} label={entry.label} value={entry.value} sub={entry.sub} color={entry.color} />
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(300px, 0.95fr)', gap: 20, alignItems: 'start' }}>
            <div style={{ display: 'grid', gap: 20 }}>
              <SectionCard title="Payment Mix">
                <BarList
                  rows={summary.paymentMix}
                  color={C.sky}
                  empty="No collected payments for this period"
                  valueLabel={(row) => `${fmtCurrency(row.amount)} · ${Number(row.pct || 0).toFixed(1)}%`}
                />
              </SectionCard>

              <SectionCard title="Top Customers">
                {summary.topCustomers.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.muted }}>No customer sales for this period.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {summary.topCustomers.map((customer, index) => (
                      <div key={customer.id || customer.name} style={{
                        display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr) auto auto',
                        gap: 12, alignItems: 'center', paddingBottom: 12,
                        borderBottom: index < summary.topCustomers.length - 1 ? `1px solid ${C.border}` : 'none',
                      }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 10,
                          background: 'rgba(240,180,41,0.12)', color: C.gold,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
                          fontFamily: 'JetBrains Mono, monospace',
                        }}>
                          {index + 1}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {customer.name}
                          </div>
                          <div style={{ fontSize: 11, color: C.muted }}>{customer.orderCount} orders</div>
                        </div>
                        <Badge variant={customer.pricingTier === 'Wholesale' ? 'navy' : customer.pricingTier === 'VIP' ? 'purple' : 'gray'}>
                          {customer.pricingTier}
                        </Badge>
                        <div style={{ textAlign: 'right' }}>
                          <div className="rp-mono" style={{ fontSize: 13, fontWeight: 700, color: C.teal }}>
                            {fmtCurrency(customer.revenue)}
                          </div>
                          <div style={{ fontSize: 11, color: customer.openBalance > 0 ? C.gold : C.muted }}>
                            {customer.openBalance > 0 ? `${fmtCurrency(customer.openBalance)} open` : 'Clear'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Top Product Movers">
                {summary.topProducts.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.muted }}>No product movement for this period.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="rp-table">
                      <thead>
                        <tr>
                          {['Product', 'Unit', 'Sold', 'Revenue', 'Margin'].map(h => (
                            <th key={h} style={{ textAlign: ['Sold','Revenue','Margin'].includes(h) ? 'right' : 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {summary.topProducts.map((product) => (
                          <tr key={product.key}>
                            <td style={{ fontWeight: 700, color: C.text }}>
                              <div>{product.name}</div>
                              <div style={{ fontSize: 11, color: C.muted }}>{product.category}</div>
                            </td>
                            <td>{product.unitName}</td>
                            <td className="rp-mono" style={{ textAlign: 'right', fontWeight: 600, color: C.text }}>
                              {Number(product.unitsSold || 0).toLocaleString('en-US')}
                            </td>
                            <td className="rp-mono" style={{ textAlign: 'right', fontWeight: 700, color: C.teal }}>
                              {fmtCurrency(product.revenue)}
                            </td>
                            <td className="rp-mono" style={{ textAlign: 'right', fontWeight: 700,
                              color: product.margin >= 20 ? C.teal : product.margin >= 8 ? C.gold : C.coral }}>
                              {Number(product.margin || 0).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </div>

            <div style={{ display: 'grid', gap: 20 }}>
              <SectionCard title="Customer Tier Sales">
                <BarList
                  rows={summary.tierSales.map((row) => ({ ...row, label: row.label }))}
                  color={C.gold}
                  empty="No tier-based sales yet"
                  valueLabel={(row) => `${fmtCurrency(row.revenue)} · ${row.orders} orders`}
                />
              </SectionCard>

              <SectionCard title="Credit Aging">
                <BarList
                  rows={summary.creditAging.map((row) => ({ ...row, label: row.label }))}
                  color={C.gold}
                  empty="No open customer balances"
                  valueLabel={(row) => `${fmtCurrency(row.amount)} · ${row.count} acct.`}
                />
              </SectionCard>

              <SectionCard title="Supplier Exposure">
                {summary.supplierExposure.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.muted }}>No supplier orders for this period.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {summary.supplierExposure.map((supplier, index) => (
                      <div key={supplier.id || supplier.name} style={{
                        paddingBottom: 12,
                        borderBottom: index < summary.supplierExposure.length - 1 ? `1px solid ${C.border}` : 'none',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{supplier.name}</div>
                          <div className="rp-mono" style={{ fontSize: 12, fontWeight: 700, color: supplier.balanceDue > 0 ? C.coral : C.teal }}>
                            {fmtCurrency(supplier.balanceDue)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, color: C.muted }}>
                          <span>Spend {fmtCurrency(supplier.periodSpend)}</span>
                          <span>Paid {fmtCurrency(supplier.paidThisPeriod)}</span>
                          <span>{supplier.openOrders} open</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 5 — EXPORT HISTORY
// ═══════════════════════════════════════════════════════════════════════════════

const TYPE_BADGES = {
  pl:        { label: 'P&L',       variant: 'green'  },
  sales:     { label: 'Sales',     variant: 'gold'   },
  inventory: { label: 'Inventory', variant: 'blue'   },
  customers: { label: 'Customer',  variant: 'purple' },
  expenses:  { label: 'Expense',   variant: 'red'    },
  wholesale: { label: 'Wholesale', variant: 'navy'   },
  vat:       { label: 'VAT',       variant: 'green'  },
  tax:       { label: 'Tax',       variant: 'navy'   },
  custom:    { label: 'Custom',    variant: 'gray'   },
};

const FORMAT_BADGES = {
  pdf:   { label: 'PDF',   variant: 'red'  },
  excel: { label: 'Excel', variant: 'blue' },
  csv:   { label: 'CSV',   variant: 'gray' },
};

function ExportHistoryTab({ onToast }) {
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [reloading, setReloading] = useState({});

  useEffect(() => {
    api.get('/reports/history?limit=100')
       .then(r => setHistory(r.data))
       .catch(() => setHistory([]))
       .finally(() => setLoading(false));
  }, []);

  async function reDownload(item) {
    setReloading(r => ({ ...r, [item.id]: true }));
    try {
      const p = item.parameters || {};
      const ext = item.format === 'excel' ? 'xlsx' : item.format === 'csv' ? 'csv' : 'pdf';
      let url, filename;

      if (item.reportType === 'pl') {
        url = buildApiUrl(`/reports/pl/pdf?month=${p.month}&year=${p.year}`);
        filename = `pl-${p.year}-${String(p.month || '').padStart(2,'0')}.pdf`;
      } else if (item.reportType === 'sales') {
        url = buildApiUrl(`/reports/sales/${item.format}?startDate=${p.startDate || ''}&endDate=${p.endDate || ''}`);
        filename = `sales-report.${ext}`;
      } else if (item.reportType === 'inventory') {
        url = buildApiUrl(`/reports/inventory/${item.format}`);
        filename = `inventory-report.${ext}`;
      } else if (item.reportType === 'customers') {
        url = buildApiUrl(`/reports/customers/${item.format}`);
        filename = `customer-report.${ext}`;
      } else if (item.reportType === 'expenses') {
        url = buildApiUrl(`/reports/expenses/${item.format}?startDate=${p.startDate || ''}&endDate=${p.endDate || ''}`);
        filename = `expense-report.${ext}`;
      } else if (item.reportType === 'wholesale') {
        url = buildApiUrl(`/reports/wholesale/${item.format}?month=${p.month}&year=${p.year}`);
        filename = `wholesale-${p.year}-${String(p.month || '').padStart(2,'0')}.${ext}`;
      } else if (item.reportType === 'vat') {
        url = buildApiUrl(`/reports/vat/pdf?quarter=${p.quarter}&year=${p.year}`);
        filename = `vat-Q${p.quarter}-${p.year}.pdf`;
      } else if (item.reportType === 'tax') {
        url = buildApiUrl(`/reports/tax/pdf?year=${p.year}`);
        filename = `tax-summary-${p.year}.pdf`;
      } else if (item.reportType === 'custom') {
        url = buildApiUrl('/reports/custom');
        filename = `custom-report.${ext}`;
        await downloadBlob(url, filename, 'POST', p);
        onToast('Report re-downloaded');
        return;
      } else {
        throw new Error('Re-download not available for this type');
      }
      await downloadBlob(url, filename);
      onToast('Report re-downloaded');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setReloading(r => ({ ...r, [item.id]: false })); }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
      <SpinIcon size={24} />
    </div>
  );

  return (
    <div className="rp-card" style={{ overflow: 'hidden' }}>
      <div className="rp-card-header">
        <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Export History</span>
        {history.length > 0 && (
          <span style={{ fontSize: 11, color: C.muted }}>{history.length} export{history.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      {history.length === 0 ? (
        <div className="rp-empty">
          <div style={{ fontSize: 52, marginBottom: 14 }}>📭</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>No reports generated yet</div>
          <div style={{ fontSize: 13, color: C.muted }}>Use the Report Center to download your first report</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="rp-table">
            <thead>
              <tr>
                {['Report Name', 'Type', 'Period', 'Format', 'Generated', 'Size', 'Action'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.map((item) => {
                const typeBadge   = TYPE_BADGES[item.reportType]   || { label: item.reportType, variant: 'gray' };
                const formatBadge = FORMAT_BADGES[item.format]     || { label: item.format,      variant: 'gray' };
                return (
                  <tr key={item.id}>
                    <td style={{ fontWeight: 600, color: C.text, whiteSpace: 'nowrap' }}>{item.reportName}</td>
                    <td><Badge variant={typeBadge.variant}>{typeBadge.label}</Badge></td>
                    <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.period}</td>
                    <td><Badge variant={formatBadge.variant}>{formatBadge.label}</Badge></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(item.createdAt)}</td>
                    <td>{fmtSize(item.fileSize)}</td>
                    <td>
                      <button
                        onClick={() => reDownload(item)}
                        disabled={reloading[item.id]}
                        style={{
                          padding: '4px 10px', borderRadius: 7,
                          background: 'transparent',
                          border: `1px solid ${C.border}`,
                          fontSize: 11, fontWeight: 600,
                          color: reloading[item.id] ? C.muted : C.text,
                          cursor: reloading[item.id] ? 'not-allowed' : 'pointer',
                          transition: 'all 0.15s', whiteSpace: 'nowrap',
                          fontFamily: 'Cabinet Grotesk, sans-serif',
                        }}
                        onMouseEnter={e => { if (!reloading[item.id]) { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; } }}
                        onMouseLeave={e => { if (!reloading[item.id]) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.text; } }}
                      >
                        {reloading[item.id] ? '…' : '⬇ Re-download'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'center',    label: 'Report Center' },
  { key: 'wholesale', label: 'Wholesale Insights' },
  { key: 'tax',       label: 'Tax Summary' },
  { key: 'custom',    label: 'Custom Builder' },
  { key: 'history',   label: 'Export History' },
];

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'center';
  const { toast, show: showToast } = useToast();

  return (
    <Layout>
      <PageTransition>
        <style>{STYLES}</style>

        <div className="rp-page">
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: '32px 24px' }}>

            {/* Header */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: C.text, margin: 0, lineHeight: 1 }}>
                  Reports &amp; Exports
                </h1>
                <span className="rp-serif" style={{ fontSize: 20, color: C.gold }}>centre</span>
              </div>
              <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
                Generate, download and track your business reports
              </p>
            </div>

            {/* Tab bar */}
            <div style={{
              display: 'flex',
              background: C.surface,
              borderRadius: '12px 12px 0 0',
              borderBottom: `2px solid ${C.border}`,
              marginBottom: 24,
              overflowX: 'auto',
            }}>
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setSearchParams({ tab: t.key })}
                  className={`rp-tab-btn${tab === t.key ? ' active' : ''}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {tab === 'center'    && <ReportCenterTab     onToast={showToast} />}
            {tab === 'wholesale' && <WholesaleInsightsTab onToast={showToast} />}
            {tab === 'tax'       && <TaxSummaryTab        onToast={showToast} />}
            {tab === 'custom'    && <CustomBuilderTab     onToast={showToast} />}
            {tab === 'history'   && <ExportHistoryTab     onToast={showToast} />}
          </div>
        </div>

        <Toast toast={toast} />
      </PageTransition>
    </Layout>
  );
}
